import React, { useMemo } from 'react';
import _ from 'lodash';

import { useAITTheme } from '@this/src/components/shared/ui/theme';
import type TransportElement from '@this/domain/transport_element2';
import type OrderItem from '@this/domain/order_item2';
import TravelerShinkansenType from '@this/src/domain/traveler/traveler_shinkansen_type';

import type { ShinkansenTicketType } from '@this/src/domain/traveler_information';
import type { ValueType } from './railway_seat_type_form';
import { convertValueType } from './railway_seat_type_form';

// number型のrailwayTicketTypeをstring型のShinkansenTicketTypeに変換
const convertNumberToShinkansenTicketType = (
  railwayTicketType: number | null | undefined
): ShinkansenTicketType | undefined => {
  switch (railwayTicketType) {
    case 0:
      return 'normal';
    case 1:
      return 'kyuusyuu';
    case 2:
      return 'bulk_ticket';
    case 3:
      return 'ex';
    default:
      return undefined;
  }
};

// number型のrailwaySeatTypeをstring型のShinkansenSeatTypeに変換
const convertNumberToShinkansenSeatType = (railwaySeatType: number | null | undefined): string | undefined => {
  switch (railwaySeatType) {
    case 0:
      return 'free';
    case 1:
      return 'reserve';
    case 2:
      return 'green';
    case 3:
      return 'bulk_ticket';
    case 4:
      return 'ex';
    case 5:
      return 'gran_class';
    default:
      return undefined;
  }
};

interface Props {
  element: TransportElement;
  orderItem: OrderItem;
  transportIndex: number;
  classNameForModifiedField: (
    pathName: 'shinkansenSeatType' | 'shinkansenTicketType',
    transportIndex: number,
    travelerIndex?: number
  ) => string;
}

export const RailwayTicketTypeForm: React.FC<Props> = ({
  element,
  orderItem,
  transportIndex,
  classNameForModifiedField
}) => {
  const { theme } = useAITTheme();

  const valueType = useMemo((): ValueType => {
    return convertValueType(element.railwaySeatType, orderItem.elements.length);
  }, [element.railwaySeatType, orderItem.elements.length]);

  if (valueType === 'singleTraveler') {
    return (
      <select
        value={element.railwayTicketType || 0}
        onChange={e => {
          element.railwayTicketType = parseInt(e.target.value, 10);
        }}
      >
        <option value={0}>通常きっぷ</option>
        <option value={1}>JR九州きっぷ</option>
        <option value={2}>回数券</option>
        <option value={3}>EX-IC</option>
      </select>
    );
  }

  return (
    <>
      {orderItem.travelerInformations.map((ti, travelerIndex) => (
        <div
          key={travelerIndex}
          className={classNameForModifiedField('shinkansenTicketType', transportIndex, travelerIndex)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: theme.tokens.colors.text.placeholder
          }}
        >
          <select
            value={travelerShinkansenTicketType(valueType, orderItem, transportIndex, travelerIndex) || 0}
            onChange={e =>
              setShinkansenTicketTypeFromNumber(
                valueType,
                orderItem,
                transportIndex,
                travelerIndex,
                parseInt(e.target.value, 10)
              )
            }
          >
            <option value={0}>通常きっぷ</option>
            <option value={1}>JR九州きっぷ</option>
            <option value={2}>回数券</option>
            <option value={3}>EX-IC</option>
          </select>
          {(travelerIndex > 0 || orderItem.travelerInformations.length > 1) && (
            <span>({ti.lastNameKana ? `${ti.lastNameKana} ${ti.firstNameKana}` : ti.user?.email})</span>
          )}
        </div>
      ))}
    </>
  );
};

const travelerShinkansenTicketType = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number
) => {
  if (orderItem.travelerInformations.length === 0) {
    return null;
  }

  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];
  const ticketType =
    valueType === 'multipleTravelersWithSeatType'
      ? traveler.travelerInformationShinkansenTypes?.[transportIndex]?.shinkansenTicketType ??
        traveler.shinkansenTicketType
      : traveler.shinkansenTicketType;

  switch (ticketType) {
    case 'kyuusyuu':
      return 1;
    case 'bulk_ticket':
      return 2;
    case 'ex':
      return 3;
    default:
      return 0;
  }
};

const setShinkansenTicketTypeFromNumber = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number,
  value: number
) => {
  if (valueType === 'multipleTravelersWithSeatType') {
    initializeTravelerShinkansenTypes(orderItem);
  }

  const shinkansenTicketTypes: Record<number, ShinkansenTicketType> = {
    0: 'normal',
    1: 'kyuusyuu',
    2: 'bulk_ticket',
    3: 'ex'
  };
  const shinkansenTicketType = shinkansenTicketTypes[value] || 'normal';
  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];

  if (valueType === 'multipleTravelersWithSeatType') {
    const shinkansenType = traveler.travelerInformationShinkansenTypes?.[transportIndex];

    if (shinkansenType) {
      shinkansenType.shinkansenTicketType = shinkansenTicketType;
    }
  } else {
    traveler.shinkansenTicketType = shinkansenTicketType;
  }

  app.render();
};

export const initializeTravelerShinkansenTypes = (orderItem: OrderItem) => {
  orderItem.travelerInformations.forEach(ti => {
    if (_.isEmpty(ti.travelerInformationShinkansenTypes)) {
      ti.travelerInformationShinkansenTypes = [];
    }

    orderItem.elements.forEach((element, elementIndex: number) => {
      if (!ti.travelerInformationShinkansenTypes[elementIndex]) {
        const el = element as TransportElement;
        const shinkansenType = new TravelerShinkansenType({
          travelerInformationId: ti.id,
          shinkansenTicketType:
            ti.shinkansenTicketType || convertNumberToShinkansenTicketType(el.railwayTicketType) || 'normal',
          shinkansenSeatType:
            ti.shinkansenSeatType || convertNumberToShinkansenSeatType(el.railwaySeatType) || 'normal'
        });
        ti.travelerInformationShinkansenTypes[elementIndex] = shinkansenType;
      }
    });
  });
};
